import type { award, ValueTypes } from '@specs/specs'
import { profile } from '@specs/specs'
import { Referrals } from '@specs/views'
import { JsonValue } from '@prisma/client/runtime/library'

export namespace ReferralProgram {
    export const startDate = new Date('2023-11-01')

    /** Param в query string, по наличию которого проверяем реферальная ли ссылка. Пример ?refferal=1234 */
    export const ReferralQuery = 'refferal'
    export const createRefferalLink = (shortid: profile['shortid']) => `${window.location.origin}/?${ReferralQuery}=${shortid}`

    export const createProdReferralLink = (shortid: profile['shortid']) => `https://artoys.app/?${ReferralQuery}=${shortid}`

    type level = 1 | 2 | 3

    type BonusKind = Exclude<ValueTypes, 'TOY' | 'MONEY'> | null

    // Referral Invite не учавствует в программе. Начисляется только при регистрации первого рефералла
    // Referral Gold Purchase начисление идет процентами а не фиксированной суммой
    type BonusInterface = {
        [K in Exclude<award, 'REFERRAL_INVITE' | 'REFERRAL_GOLD_PURCHASE'>]: { kind: BonusKind; amount: number }
    }

    type BonusPercentInterface = {
        [K in Extract<award, 'REFERRAL_GOLD_PURCHASE'>]: { kind: BonusKind; amount: number }
    }

    type BonusOptionInterface = Record<level, BonusInterface>

    type BonusOptionPercentInterface = Record<level, BonusPercentInterface>

    // TODO: после запуска скрипта, убрать REFERRAL_SUBSCIPTION из всех уровней
    const BonusOptions: BonusOptionInterface = {
        1: {
            REFFERAL_5DROPS: { kind: 'GOLD', amount: 100 },
            REFERRAL_SUBSCRIBTION: { kind: null, amount: 0 },
            REFERRAL_SUBSCRIBTION_MONTH: { kind: null, amount: 0 },
            REFERRAL_SUBSCRIBTION_HALF_YEAR: { kind: null, amount: 0 },
            REFERRAL_SUBSCRIBTION_YEAR: { kind: null, amount: 0 },
        },
        2: {
            REFFERAL_5DROPS: { kind: 'GOLD', amount: 500 },
            REFERRAL_SUBSCRIBTION: { kind: null, amount: 0 },
            REFERRAL_SUBSCRIBTION_MONTH: { kind: 'GOLD', amount: 100 },
            REFERRAL_SUBSCRIBTION_HALF_YEAR: { kind: 'GOLD', amount: 250 },
            REFERRAL_SUBSCRIBTION_YEAR: { kind: 'GOLD', amount: 500 },
        },
        3: {
            REFFERAL_5DROPS: { kind: 'GOLD', amount: 1000 },
            REFERRAL_SUBSCRIBTION: { kind: null, amount: 0 },
            REFERRAL_SUBSCRIBTION_MONTH: { kind: 'RUBY', amount: 400 },
            REFERRAL_SUBSCRIBTION_HALF_YEAR: { kind: 'RUBY', amount: 2000 },
            REFERRAL_SUBSCRIBTION_YEAR: { kind: 'RUBY', amount: 3000 },
        },
    }

    const BonusPercentOptions: BonusOptionPercentInterface = {
        1: {
            REFERRAL_GOLD_PURCHASE: { kind: null, amount: 0 },
        },
        2: {
            REFERRAL_GOLD_PURCHASE: { kind: 'GOLD', amount: 30 },
        },
        3: {
            REFERRAL_GOLD_PURCHASE: { kind: 'RUBY', amount: 20 },
        },
    }

    export const BonusInvite = {
        kind: 'GOLD' as const,
        amount: 100,
        award: 'REFERRAL_INVITE',
    }

    export const getAwardTypesByLevel = (level: level) => BonusOptions[level]

    export const getAwardByType = (type: award, level: level) => BonusOptions[level][type]

    export const getAwardByPercentType = (type: award, level: level) => BonusPercentOptions[level][type]

    export enum Level {
        FIRST = 1,
        SECOND = 2,
        THIRD = 3,
    }

    export const getLevel = (referrals: number) => {
        switch (true) {
            case referrals < 10:
                return 1
            case referrals >= 10 && referrals < 100:
                return 2
            case referrals >= 100:
                return 3
            default:
                return 1
        }
    }

    export type referralLevelBodyType = {
        level: level
    }
    export type referralBonusBodyType = {
        currency: NonNullable<BonusKind>
        amount: number
        award: award
    }

    export const toFeedJson = (feed: referralLevelBodyType | referralBonusBodyType) => JSON.stringify(feed)
    // export const getDictionary = (award: award): Dictionary | undefined => {
    //     switch (award) {
    //         case 'REFFERAL_5DROPS':
    //             return 'referral.page.condition.collected.toy'
    //         case 'REFERRAL_INVITE':
    //             return 'referral.page.condition.provided.referral'
    //         case 'REFERRAL_SUBSCRIBTION_MONTH':
    //             return 'referral.page.condition.subscription.month'
    //         case 'REFERRAL_SUBSCRIBTION_HALF_YEAR':
    //             return 'referral.page.condition.subscription.six'
    //         case 'REFERRAL_SUBSCRIBTION_YEAR':
    //             return 'referral.page.condition.subscription.year'
    //         case 'REFERRAL_GOLD_PURCHASE':
    //             return 'referral.page.condition.bought.gold'
    //         case 'REFERRAL_SUBSCRIBTION':
    //             return undefined
    //         default:
    //             exhaustiveCheck(award)
    //     }
    // }

    // export const getSubsciptionDictionary = (
    //     award?: award
    // ):
    //     | {
    //           id: Dictionary
    //           count: string | number
    //       }
    //     | undefined => {
    //     switch (award) {
    //         case 'REFERRAL_SUBSCRIBTION_MONTH':
    //             return {
    //                 id: 'subscription.page.month',
    //                 count: ''
    //             }
    //         case 'REFERRAL_SUBSCRIBTION_HALF_YEAR':
    //             return {
    //                 id: 'subscription.page.months',
    //                 count: 6
    //             }
    //         case 'REFERRAL_SUBSCRIBTION_YEAR':
    //             return {
    //                 id: 'subscription.page.year',
    //                 count: ''
    //             }
    //         default:
    //             undefined
    //     }
    // }

    /** ADMIN */

    /** Типы реферальной системы */
    export const referralTypes = [
        { id: 'COMMON', name: 'Обычный' },
        { id: 'PRIVILEGED', name: 'Привилегированный' },
    ]

    export const referralTypeDictionary = {
        COMMON: 'Обычный',
        PRIVILEGED: 'Привилегированный',
    }

    /** Дополнительный бонусы за приглашение */
    export const referralBonuses: Array<{
        id: InviteBonusesType
        name: string
    }> = [
        { id: 'SUBSCRIBTION', name: 'Подписка' },
        { id: 'RUBY', name: 'Руби' },
        { id: 'GOLD', name: 'Голда' },
    ]

    export type InviteBonusesType = 'GOLD' | 'RUBY' | 'SUBSCRIBTION'

    export type SubscriptionType = 'MONTH' | 'HALF_YEAR' | 'YEAR'

    export const getInviteBonuses = (bonuses: JsonValue | undefined) => {
        const parsed = JSON.parse((bonuses as string) ?? '') as Referrals.InviteBonus[]

        if (!Array.isArray(parsed) || !parsed.length) {
            return null
        }

        return parsed
    }
}
